import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from 'react';
import * as PropTypes from 'prop-types';
import classNames from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dropdown, mergeStyleSets } from '@fluentui/react';
import { Constants } from '@shared/utils/constants';
import { windowScrollToTop } from '@shared/utils/filterHelpers';
import { logger } from '@src/logger';
import { BusinessTelemetry } from '@shared/utils/businessConstants';
import sortByImage from '@shared/images/sortByIcon.svg';
const DataBi = BusinessTelemetry.dataBi;
const $backgroundColor = '#ffffff';
const $primaryText = '#323130';
const contentStyles = mergeStyleSets({
    dropdownWidth: {
        minWidth: '150px'
    },
    SearchSortBy: {
        display: 'flex',
        fontSize: '14px',
        lineHeight: '20px',
        marginTop: 'auto',
        paddingBottom: '4px',
        '& .spza-sub-filter-dropdown': {
            '& .valueBox': {
                outline: 'none',
                width: 'max-content',
                border: 'none',
                '& .item': {
                    padding: '3px 7px 3px 3px'
                },
                '.item > span': {
                    width: 'max-content'
                },
                '.toggle': {
                    width: '24px'
                }
            }
        }
    },
    sortByIconAndText: {
        color: {
            $primaryText
        },
        margin: '0px 5px 0px 0px',
        backgroundSize: '16px 16px',
        padding: '5px 0px 0px 24px',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'left',
        backgroundColor: {
            $backgroundColor
        },
        backgroundImage: `url(${sortByImage})`,
        '& .ms-Dropdown:not(.is-open)': {
            '& .ms-Dropdown-title': {
                borderWidth: '0px'
            }
        }
    }
});
export const SortingDropdown = ({ defaultKey, route, hide }, context)=>{
    const [isOpen, setIsOpen] = useState(false);
    const [searchByKey, setSearchByKey] = useState(defaultKey);
    const isSortingSupported = route.name !== Constants.Views.cloudsIndustryGallery;
    const location = useLocation();
    const navigate = useNavigate();
    const getSortingOptions = ()=>{
        let options = [
            {
                text: context.loc('AppsGlobalFilters_SearchSortingOptions_BestMatch', 'Best match'),
                key: Constants.SearchSortingOption.BestMatch
            },
            {
                text: context.loc('AppsGlobalFilters_SearchSortingOptions_Popularity', 'Most viewed'),
                key: Constants.SearchSortingOption.Popularity
            }
        ];
        const areNewSortsSupported = route.name === Constants.Views.appGallery;
        if (areNewSortsSupported) {
            options = [
                ...options,
                {
                    text: context.loc('AppsGlobalFilters_SearchSortingOptions_Rating', 'Average rating'),
                    key: Constants.SearchSortingOption.RatingAverage
                },
                {
                    text: context.loc('AppsGlobalFilters_SearchSortingOptions_LastUpdated', 'Recent version update '),
                    key: Constants.SearchSortingOption.RecentlyUpdated
                }
            ];
        }
        return options;
    };
    const options = getSortingOptions();
    const onSortingChangeHandle = (option)=>{
        const payload = {
            page: location.pathname,
            action: Constants.Telemetry.Action.Click,
            actionModifier: Constants.Telemetry.ActionModifier.SortSearchResults,
            details: option.key
        };
        logger.info(payload.details, payload);
        const sortingLink = context.buildHref(route, null, {
            sortBy: option.key === Constants.SearchSortingOption.BestMatch ? null : option.key
        });
        // Set the state for display to update immediately (testability)
        setSearchByKey(option.key);
        // Navigate to the URL for functionality
        navigate(sortingLink);
    };
    const onSortingClickHandle = ()=>{
        const payload = {
            page: location.pathname,
            action: Constants.Telemetry.Action.Click,
            actionModifier: Constants.Telemetry.ActionModifier.SortSearchOpened
        };
        logger.info('', payload);
        if (!isOpen) {
            setIsOpen((isOpen)=>!isOpen);
        }
    };
    const detectKeyboardClick = ({ event: { key } })=>{
        const currentOptionsIndex = options.findIndex((option)=>option.key === searchByKey);
        if (key === Constants.SystemKeyAsString.Enter || key === Constants.SystemKeyAsString.Space) {
            onSortingChangeHandle(options[`${currentOptionsIndex}`]);
        }
        if (key === Constants.SystemKeyAsString.ArrowUp) {
            const nextIdx = currentOptionsIndex - 1 <= 0 ? 0 : currentOptionsIndex - 1;
            setSearchByKey(options[`${nextIdx}`].key);
        }
        if (key === Constants.SystemKeyAsString.ArrowDown) {
            const nextIdx = currentOptionsIndex + 1 >= options.length ? options.length : currentOptionsIndex + 1;
            setSearchByKey(options[`${nextIdx}`].key);
        }
    };
    const onDropdownChange = ({ option })=>{
        onSortingChangeHandle(option);
        windowScrollToTop();
    };
    const activeSortByOption = options.filter((option)=>option.key === searchByKey)[0] || options[0];
    const sortingDropdownClass = classNames({
        [contentStyles.dropdownWidth]: isOpen
    });
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: !hide && isSortingSupported && /*#__PURE__*/ _jsxs("div", {
            className: contentStyles.SearchSortBy,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: contentStyles.sortByIconAndText,
                    children: context.loc('AppsGlobalFilters_SearchSortingOptions_Title', 'Sort By:')
                }),
                /*#__PURE__*/ _jsx(Dropdown, {
                    "data-bi-id": DataBi.sortingDropdownId,
                    "data-bi-name": DataBi.sortingDropdownName,
                    selectedKey: activeSortByOption.key,
                    options: options,
                    onKeyDown: (event)=>detectKeyboardClick({
                            event
                        }),
                    ariaLabel: context.loc('AppsGlobalFilters_SearchSortingOptions_AriaLabel', 'Sort by'),
                    className: sortingDropdownClass,
                    onChange: (_, option)=>onDropdownChange({
                            option
                        }),
                    onClick: onSortingClickHandle,
                    onDismiss: ()=>{
                        setIsOpen((isOpen)=>!isOpen);
                    },
                    selectedKeys: [
                        activeSortByOption.key.toString()
                    ]
                })
            ]
        })
    });
};
SortingDropdown.contextTypes = {
    loc: PropTypes.func,
    buildHref: PropTypes.func
};
